/**
 * Centralized React Query hooks for invoice fetches.
 *
 * Why this file exists: before extraction, four near-identical Supabase
 * queries lived inline across Dashboard, InvoiceDetail, EditInvoice and
 * AppHeader (prefetch). Each spelled out the same `from("invoices")` /
 * `from("invoice_items")` chain, so a tiny change (column added, sort
 * order tweaked, query key renamed) had to be repeated in 4+ places and
 * silently drifted out of sync — most notably between the Dashboard
 * fetcher and the AppHeader hover-prefetch, where a key mismatch would
 * cause the prefetch to silently miss.
 *
 * The hooks below are the single source of truth for those reads. The
 * EXACT query keys are also exported so `queryClient.prefetchQuery` /
 * `invalidateQueries` callers (AppHeader, mutations) can reuse them
 * without the risk of typo'd keys.
 *
 * Caching defaults match the previous inline behaviour:
 *   - list/stats: staleTime 30s (matches AppHeader prefetch)
 *   - single invoice / items in EditInvoice: caller can pass overrides
 *     (Infinity, no refetch on focus) via the options arg.
 */
import { useQuery, useQueryClient, type QueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

export const INVOICES_PAGE_SIZE = 20;

type Invoice = Tables<"invoices">;
type InvoiceItem = Tables<"invoice_items">;
type InvoiceStatus = Invoice["status"];

// ---------- Query keys (exported so prefetch/invalidate callers stay in sync) ----------
export const invoiceKeys = {
  stats: (userId: string | undefined) => ["invoices-stats", userId] as const,
  page: (
    userId: string | undefined,
    statusFilter: InvoiceStatus | null,
    page: number,
  ) => ["invoices-page", userId, statusFilter, page] as const,
  detail: (id: string | undefined) => ["invoice", id] as const,
  items: (id: string | undefined) => ["invoice-items", id] as const,
};

/**
 * Invalidate every invoice list/detail query after a mutation
 * (insert / update / delete / status change). Call this in mutation
 * handlers so the Dashboard, status counters and detail page all
 * refresh immediately instead of waiting for `staleTime` to expire.
 *
 * Use the hook variant inside React components; the standalone
 * function is for non-component callers (e.g. utilities).
 */
export function invalidateInvoiceQueries(qc: QueryClient, invoiceId?: string) {
  qc.invalidateQueries({ queryKey: ["invoices-stats"] });
  qc.invalidateQueries({ queryKey: ["invoices-page"] });
  if (invoiceId) {
    qc.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
    qc.invalidateQueries({ queryKey: invoiceKeys.items(invoiceId) });
  }
}

export function useInvalidateInvoices() {
  const qc = useQueryClient();
  return (invoiceId?: string) => invalidateInvoiceQueries(qc, invoiceId);
}

// ---------- Stats: fetch every invoice (id+status only) for status counters ----------
export const fetchInvoicesStats = async () => {
  const { data, error } = await supabase
    .from("invoices")
    .select("id, status")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data;
};

export function useInvoicesStats(userId: string | undefined) {
  return useQuery({
    queryKey: invoiceKeys.stats(userId),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
    queryFn: fetchInvoicesStats,
  });
}

// ---------- Paginated list with optional status filter ----------
export const fetchInvoicesPage = async (
  statusFilter: InvoiceStatus | null,
  page: number,
) => {
  let query = supabase
    .from("invoices")
    .select("*", { count: "exact" })
    .order("created_at", { ascending: false });
  if (statusFilter) query = query.eq("status", statusFilter);
  const from = page * INVOICES_PAGE_SIZE;
  const to = from + INVOICES_PAGE_SIZE - 1;
  query = query.range(from, to);
  const { data, error, count } = await query;
  if (error) throw error;
  return { data: (data ?? []) as Invoice[], count: count ?? 0 };
};

export function useInvoicesPage(
  userId: string | undefined,
  statusFilter: InvoiceStatus | null,
  page: number,
) {
  return useQuery({
    queryKey: invoiceKeys.page(userId, statusFilter, page),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
    queryFn: () => fetchInvoicesPage(statusFilter, page),
  });
}

// ---------- Single invoice (detail / edit) ----------
type SingleInvoiceOptions = Omit<
  UseQueryOptions<Invoice, Error, Invoice, ReturnType<typeof invoiceKeys.detail>>,
  "queryKey" | "queryFn" | "enabled"
> & { enabled?: boolean };

export function useInvoice(id: string | undefined, options?: SingleInvoiceOptions) {
  return useQuery({
    queryKey: invoiceKeys.detail(id),
    enabled: (options?.enabled ?? true) && !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data as Invoice;
    },
    ...options,
  });
}

// ---------- Invoice items (line items) ----------
type InvoiceItemsOptions = Omit<
  UseQueryOptions<InvoiceItem[], Error, InvoiceItem[], ReturnType<typeof invoiceKeys.items>>,
  "queryKey" | "queryFn" | "enabled"
> & { enabled?: boolean };

export function useInvoiceItems(id: string | undefined, options?: InvoiceItemsOptions) {
  return useQuery({
    queryKey: invoiceKeys.items(id),
    enabled: (options?.enabled ?? true) && !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoice_items")
        .select("*")
        .eq("invoice_id", id!)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as InvoiceItem[];
    },
    ...options,
  });
}
